import React, { useState, useEffect } from "react";
import { PLAY_TYPES, PLAY_FORMATS } from "../constants";
import DateSelector from "./DateSelector";
import TimePicker from "./TimePicker";
import PropTypes from "prop-types";

export default function ProposePlayModal({
  show,
  onClose,
  onSubmit,
  formData,
  onChange,
  groupOptions,
  isEditing = false,
  userSkillLevel,
  isSubmitting = false
}) {
  const [errors, setErrors] = useState({});

  // Auto-set group ID when there's only one group available
  useEffect(() => {
    if (groupOptions.length === 1 && !formData.group) {
      onChange({ target: { name: 'group', value: groupOptions[0].id } });
    }
  }, [groupOptions, formData.group, onChange]);

  // Auto-select Singles format for both Match and Just for Fun types
  useEffect(() => {
    if ((formData.typeOfPlay === PLAY_TYPES.MATCH || formData.typeOfPlay === PLAY_TYPES.PRACTICE) && !formData.format) {
      onChange({ target: { name: 'format', value: PLAY_FORMATS.SINGLES } });
    }
  }, [formData.typeOfPlay, formData.format, onChange]);

  if (!show) return null;

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.group) newErrors.group = 'Please select a group';
    if (!formData.typeOfPlay) newErrors.typeOfPlay = 'Please select type of play';
    if (!formData.format) newErrors.format = 'Please select play style';
    if (!formData.date) newErrors.date = 'Please select a date';
    if (!formData.time) newErrors.time = 'Please select a time';
    if (!formData.location) newErrors.location = 'Please enter a location';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted with data:", formData);
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field, value) => {
    // Clear error when field is changed
    setErrors(prev => ({ ...prev, [field]: undefined }));
    onChange({ target: { name: field, value } });
  };

  const optionTile = (name, value, currentValue, label) => (
    <button
      type="button"
      onClick={() => handleChange(name, value)}
      className={`px-4 py-2 rounded-lg border ${
        currentValue === value
          ? 'bg-primary/5 border-primary text-primary'
          : 'border-border text-textMed hover:bg-primary/5'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg space-y-6 overflow-y-auto max-h-[90vh] touch-auto">
        <h3 className="text-xl font-bold text-center mb-4 text-textDark">
          {isEditing ? 'Edit Proposal' : 'Propose New Play'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold mb-2 text-textDark" htmlFor="group">
              Choose Group <span className="text-red-600">*</span>
            </label>
            {groupOptions.length === 0 ? (
              <div className="w-full border border-gray-200 rounded-md p-4 bg-gray-50 text-center">
                <div className="text-gray-500 mb-2">
                  <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className="text-gray-700 font-medium mb-2">No Groups Available</p>
                <p className="text-gray-500 text-sm">Join a group to propose a time to play</p>
              </div>
            ) : groupOptions.length === 1 ? (
              <input
                type="text"
                id="group"
                name="group"
                value={groupOptions[0].name}
                className="w-full border border-border rounded-md p-2 bg-gray-50 cursor-not-allowed text-textMed"
                disabled
              />
            ) : (
              <>
                <select
                  name="group"
                  id="group"
                  value={formData.group}
                  onChange={(e) => handleChange('group', e.target.value)}
                  className={`w-full border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-accent ${
                    errors.group ? 'border-red-500' : 'border-border'
                  }`}
                  required
                >
                  <option value="">Select group...</option>
                  {groupOptions.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
                {errors.group && (
                  <p className="mt-1 text-sm text-red-600">{errors.group}</p>
                )}
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              Type of Play <span className="text-red-600">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {optionTile("typeOfPlay", PLAY_TYPES.MATCH, formData.typeOfPlay, "Match")}
              {optionTile("typeOfPlay", PLAY_TYPES.PRACTICE, formData.typeOfPlay, "Just for Fun")}
            </div>
            {errors.typeOfPlay && (
              <p className="mt-1 text-sm text-red-600">{errors.typeOfPlay}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              Play Style <span className="text-red-600">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {optionTile("format", PLAY_FORMATS.SINGLES, formData.format, "Singles")}
              {/* Doubles option can be uncommented when supported */}
              {/* {optionTile("format", PLAY_FORMATS.DOUBLES, formData.format, "Doubles")} */}
            </div>
            {errors.format && (
              <p className="mt-1 text-sm text-red-600">{errors.format}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              Date <span className="text-red-600">*</span>
            </label>
            <DateSelector
              selectedDate={formData.date}
              onDateSelect={(date) => handleChange("date", date)}
            />
            {errors.date && (
              <p className="mt-1 text-sm text-red-600">{errors.date}</p>
            )}
          </div>

          <div>
            <TimePicker
              selectedTime={formData.time}
              onTimeSelect={(time) => handleChange("time", time)}
            />
            {errors.time && (
              <p className="mt-1 text-sm text-red-600">{errors.time}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2" htmlFor="location">
              Location <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="location"
              name="location"
              placeholder="Enter location"
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              className={`w-full border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-accent ${
                errors.location ? 'border-red-500' : 'border-border'
              }`}
              required
            />
            {errors.location && (
              <p className="mt-1 text-sm text-red-600">{errors.location}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2" htmlFor="comments">
              Comments
            </label>
            <textarea
              id="comments"
              name="comments"
              placeholder="Flexible on start time, other locations, and court reserved"
              value={formData.comments}
              onChange={(e) => handleChange('comments', e.target.value)}
              className="w-full border border-border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-accent resize-none"
              rows={3}
            />
          </div>

          {/* Loading message */}
          {isSubmitting && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center space-x-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <p className="text-blue-700 font-medium">
                  Creating proposal and sending notifications...
                </p>
              </div>
              <p className="text-blue-600 text-sm mt-2">
                This may take a few moments as we notify all group members.
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className={`px-4 py-2 border border-primary text-primary rounded-lg ${
                isSubmitting 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-primary/5'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || groupOptions.length === 0}
              className={`px-4 py-2 bg-primary text-white rounded-lg flex items-center space-x-2 ${
                isSubmitting || groupOptions.length === 0
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-primaryDark'
              }`}
            >
              {isSubmitting && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <span>
                {isSubmitting 
                  ? 'Creating...' 
                  : isEditing 
                    ? 'Save Changes' 
                    : 'Create Proposal'
                }
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

ProposePlayModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  groupOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  })).isRequired,
  isEditing: PropTypes.bool,
  userSkillLevel: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool
};